// Admins make a location a vendor's CM site: an ignored Shopify location (Tailhoo Technology, Elecrow), whose
// Shopify stock then comes in, one of the vendor's own sites, or a new site that isn't in Shopify.
import { useMemo, useState } from "react";
import { COUNT_POLICIES, COUNT_POLICY_LABELS, type CountPolicy } from "../../../../shared/consignment.ts";
import { mutateWithResult } from "../../lib/mutate.ts";
import { setSearch } from "../../lib/router.tsx";
import { useRows } from "../../lib/sync.ts";
import { Button, Input, Select } from "../../ui/controls.tsx";
import { toast } from "../../ui/toast.tsx";
import { Modal } from "./parts.tsx";

const NEW = "new";

export function SetupCmSite({ preset, onClose }: { preset?: string; onClose: () => void }) {
  const locations = useRows("location");
  const vendors = useRows("vendor");
  const candidates = useMemo(
    () =>
      locations
        .filter((l) => !l.is_cm && (l.kind === "vendor_site" || (l.kind === "hhl" && l.shopify_location_id)))
        .sort((a, b) => Number(!a.ignored) - Number(!b.ignored) || a.name.localeCompare(b.name)),
    [locations],
  );
  const [locationId, setLocationId] = useState(preset && candidates.some((c) => c.id === preset) ? preset : (candidates[0]?.id ?? NEW));
  const chosen = candidates.find((c) => c.id === locationId);
  const cms = useMemo(
    () =>
      vendors
        .filter((v) => !v.archived)
        .sort((a, b) => Number(a.kind === "supplier") - Number(b.kind === "supplier") || (a.short_name || a.name).localeCompare(b.short_name || b.name)),
    [vendors],
  );
  // A guess from the location's name (Tailhoo Technology → Tailhoo), else its vendor, else the first CM.
  const guess = (id: string) => {
    const loc = candidates.find((c) => c.id === id);
    if (loc?.vendor_id) return loc.vendor_id;
    const words = (loc?.name ?? "")
      .toLowerCase()
      .split(/\s+/)
      .filter((w) => w.length > 3);
    return cms.find((v) => words.some((w) => `${v.name} ${v.short_name}`.toLowerCase().includes(w)))?.id ?? cms.find((v) => v.kind !== "supplier")?.id ?? "";
  };
  const [vendorId, setVendorId] = useState(() => guess(locationId));
  const [name, setName] = useState("");
  const [policy, setPolicy] = useState<CountPolicy>("monthly");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!vendorId) return toast.error("Choose the vendor");
    if (locationId === NEW && !name.trim()) return toast.error("Name the new site");
    setBusy(true);
    const newId = crypto.randomUUID();
    const { ok, result } = await mutateWithResult(
      "location.make_cm_site",
      locationId === NEW
        ? { name: name.trim(), new_id: newId, vendor_id: vendorId, count_policy: policy }
        : { id: locationId, vendor_id: vendorId, count_policy: policy },
    );
    setBusy(false);
    if (!ok) return;
    const r = result as { id: string; shopify: boolean };
    if (r.shopify) {
      // Bring its Shopify stock in now rather than at the next 6-hourly pull (best effort).
      fetch("/api/shopify/run", {
        method: "POST",
        headers: { "content-type": "application/json", "x-requested-with": "hhl-ops" },
        body: JSON.stringify({ job: "stock" }),
      }).catch(() => {});
    }
    toast.ok(r.shopify ? "CM site set up; its Shopify stock is being pulled" : "CM site set up");
    setSearch({ cm: r.id, setup: null });
    onClose();
  };

  return (
    <Modal
      title="Set up a CM site"
      onClose={onClose}
      width={520}
      footer={
        <>
          <Button variant="ghost" className="ml-auto" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={busy} onClick={submit}>
            Make it a CM site
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 p-4">
        <label className="flex flex-col gap-1">
          <span className="text-[12px] text-muted">Location</span>
          <Select
            value={locationId}
            onChange={(e) => {
              setLocationId(e.target.value);
              if (e.target.value !== NEW) setVendorId(guess(e.target.value));
            }}
            options={[
              ...candidates.map((l) => ({
                value: l.id,
                label: `${l.name}${l.shopify_location_id ? (l.ignored ? " (Shopify, not used)" : " (Shopify)") : " (vendor site)"}`,
              })),
              { value: NEW, label: "A new site, not in Shopify…" },
            ]}
          />
        </label>
        {locationId === NEW && (
          <label className="flex flex-col gap-1">
            <span className="text-[12px] text-muted">Site name</span>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Elecrow Shenzhen" autoFocus />
          </label>
        )}
        <label className="flex flex-col gap-1">
          <span className="text-[12px] text-muted">Contract manufacturer</span>
          <Select
            value={vendorId}
            onChange={(e) => setVendorId(e.target.value)}
            options={[{ value: "", label: "Choose a vendor…" }, ...cms.map((v) => ({ value: v.id, label: v.short_name || v.name }))]}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[12px] text-muted">Ask for a count</span>
          <Select
            value={policy}
            onChange={(e) => setPolicy(e.target.value as CountPolicy)}
            options={COUNT_POLICIES.map((p) => ({ value: p, label: COUNT_POLICY_LABELS[p] }))}
          />
        </label>
        <div className="text-[12px] text-muted">
          {chosen?.shopify_location_id
            ? `${chosen.name} is a Shopify location: Shopify items' stock there keeps coming from Shopify (only Shopify changes it), and HHL Ops' own parts there are kept in its ledger.`
            : "HHL Ops keeps the stock of its own parts there in its ledger. Shopify items can't be held at a site that isn't a Shopify location."}
        </div>
      </div>
    </Modal>
  );
}
